import { useQueryClient } from "@tanstack/react-query"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CopyIcon,
  DownloadIcon,
  FileTextIcon,
  InfoIcon,
  MailIcon,
  OctagonAlertIcon,
  PencilIcon,
  Share2Icon,
  TriangleAlertIcon,
} from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { panFromGstin, type ConversionPreviewRateChange } from "@steelbill/types"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { DateField } from "@/components/DateField"
import { SyncStatus } from "@/components/SyncStatus"
import { DocumentStatusBadge, useStatusLabel } from "@/features/invoices/DocumentStatusBadge"
import {
  useConvertPreview,
  useConvertQuotation,
  useEmailQuotation,
  useQuotation,
  useQuotationPdf,
  useSetQuotationStatus,
  useVoidQuotation,
  type ConvertPreviewResult,
} from "@/features/quotations/use-quotation"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { QuotationApprovalCard } from "@/features/quotations/QuotationApprovalCard"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"
import { requestBlob } from "@/lib/api"
import { shareDocument } from "@/lib/share"
import { useFormatDay } from "@/lib/dates"
import { endpoints, type DocumentLine, type QuotationDetail } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * One quotation (§7, §7.1, §9.3, §11.3).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * HOW THIS DIFFERS FROM THE BILL SCREEN, AND WHY EACH DIFFERENCE EXISTS.
 *
 * IT OFFERS TO EDIT. The bill screen does not, and that absence is deliberate
 * (§2.1 defines no `invoice.edit` for anyone). A quotation is an OFFER, and
 * revising one before it is accepted is ordinary business — so "Edit" is here,
 * gated on the server's own `isEditable` rather than a rule re-derived on this
 * screen, and it VANISHES rather than greying out once the offer has been
 * answered, converted, expired or voided.
 *
 * IT PRINTS THE FIGURE THE CUSTOMER WAS QUOTED. §9.3 — under TAX_EXTRA the sheet
 * quotes the PRE-TAX subtotal with "GST EXTRA" against the tax rows, so the
 * number the customer holds is NOT `grandTotalPaise`. The server resolves which
 * one that is into `display.printedGrandTotalPaise`, and this screen renders
 * that field. It does not choose between the two totals itself; every past
 * attempt to do so on some other screen has picked wrong.
 *
 * VOID, NOT CANCEL. Withdrawing an offer carries no statutory meaning and needs
 * no credit note — but the row survives with its number, because the quotation
 * series is gap-free too (§6).
 *
 * AND IT CONVERTS. `POST /:id/convert` is the one action here that ALLOCATES AN
 * INVOICE NUMBER and raises a real tax document (§7.1). It is treated with the
 * weight that deserves: a confirmation, and never a second click.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * EVERY FIGURE COMES FROM THE SERVER (§6). The one arithmetic operation in this
 * file is `qtyMilli / 1000` for display, commented where it happens.
 */

/** Matched to `buttonVariants({ variant: "outline" })` — see the bill screen. */
const TRIGGER_AS_BUTTON =
  "h-8 gap-1.5 rounded-lg border border-border bg-background px-2.5 hover:bg-muted hover:text-foreground data-popup-open:bg-muted data-open:bg-muted"

/**
 * The statuses from which a conversion still makes sense (§7.1).
 *
 * PARTIALLY_CONVERTED IS IN THE LIST: a staged job is invoiced in instalments
 * against one offer, and the remaining lines are still billable. CONVERTED is
 * not — every line already carries a bill. The server enforces this regardless;
 * the list decides whether the button is OFFERED, never whether it is allowed.
 */
const CONVERTIBLE = new Set(["DRAFT", "SENT", "APPROVED", "PARTIALLY_CONVERTED"])

/**
 * States in which the offer is already finished with — so there is nothing left
 * to cancel.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * "IF QUOTATION IS CLOSED THERE IS NO MEANING TO SHOW A CANCELLED BUTTON."
 *
 * Cancelling withdraws an offer that is still standing. Once the customer has
 * REJECTED it, or it has EXPIRED unanswered, or it has become a bill, there is
 * no offer left to withdraw — the button would write a void reason against a
 * document nobody is going to act on, and voiding is TERMINAL and unreversible.
 *
 * VOID is here too, for the obvious reason.
 *
 * The server refuses these regardless; this decides what is OFFERED, never what
 * is allowed. And it HIDES rather than disables: a greyed-out Cancel on a
 * rejected quotation invites the question of how to un-grey it, and the honest
 * answer — nothing, it is over — is better said by the control not being there.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const CLOSED = new Set(["VOID", "REJECTED", "EXPIRED", "CONVERTED", "PARTIALLY_CONVERTED"])

/** The statuses a human sets by hand (§7) — `MANUAL_QUOTATION_STATUSES`. */
const NEXT_STATUSES: { status: string; labelKey: string; helpKey: string }[] = [
  { status: "SENT", labelKey: "quotation.markSent", helpKey: "quotation.markSentHelp" },
  { status: "APPROVED", labelKey: "quotation.markApproved", helpKey: "quotation.markApprovedHelp" },
  { status: "REJECTED", labelKey: "quotation.markRejected", helpKey: "quotation.markRejectedHelp" },
  { status: "EXPIRED", labelKey: "quotation.markExpired", helpKey: "quotation.markExpiredHelp" },
]

/**
 * The banner a locked quotation carries, keyed by status.
 *
 * ONE ENTRY PER TERMINAL STATE, EACH WORDED FOR ITS OWN REASON. A single "this
 * quotation cannot be edited" would be true and useless: the owner's next step
 * differs completely between "the customer approved it" (convert it) and "it
 * lapsed" (copy it into a fresh offer), and the banner is where he reads which.
 */
const LOCK_NOTICES: Record<string, { titleKey: string; helpKey: string; danger: boolean }> = {
  APPROVED: {
    titleKey: "quotation.lockedApprovedTitle",
    helpKey: "quotation.lockedApprovedHelp",
    danger: false,
  },
  REJECTED: {
    titleKey: "quotation.lockedRejectedTitle",
    helpKey: "quotation.lockedRejectedHelp",
    danger: false,
  },
  EXPIRED: {
    titleKey: "quotation.lockedExpiredTitle",
    helpKey: "quotation.lockedExpiredHelp",
    danger: false,
  },
  PARTIALLY_CONVERTED: {
    titleKey: "quotation.lockedPartlyTitle",
    helpKey: "quotation.lockedPartlyHelp",
    danger: false,
  },
  CONVERTED: {
    titleKey: "quotation.lockedConvertedTitle",
    helpKey: "quotation.lockedConvertedHelp",
    danger: false,
  },
  VOID: {
    titleKey: "quotation.lockedVoidTitle",
    helpKey: "quotation.lockedVoidHelp",
    danger: true,
  },
}

export function QuotationDetailScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()
  const formatDay = useFormatDay()
  const queryClient = useQueryClient()

  const query = useQuotation(id)
  const quotation = query.data
  const pdf = useQuotationPdf(id, quotation?.number)
  const setStatus = useSetQuotationStatus(id)
  /*
   * DECLARED WITH THE OTHER HOOKS, ABOVE THE EARLY RETURNS — see the note on
   * `converting` below for what happens otherwise ("Rendered more hooks than
   * during the previous render", for every test in the file).
   */
  const email = useEmailQuotation(id)

  const [sharing, setSharing] = useState(false)
  /*
   * DECLARED HERE WITH THE OTHER HOOKS, ABOVE THE EARLY RETURNS.
   *
   * It first sat lower down beside `convertIsThePoint`, which reads better but
   * is after `if (query.isPending) return <DetailSkeleton />` — so the hook was
   * skipped on the loading render and called on the next one, and React threw
   * "Rendered more hooks than during the previous render" for EVERY test in the
   * file. Hooks run unconditionally or not at all.
   */
  const [converting, setConverting] = useState(false)
  /** Open after a share that MIGHT have gone out — see the note in `onShare`. */
  const [askSent, setAskSent] = useState(false)
  const onShare = async () => {
    if (quotation === undefined || id === undefined) return
    setSharing(true)
    try {
      const filename = `${quotation.number.replace(/\//g, "-")}.pdf`
      const fetched = await requestBlob(endpoints.quotationPdf(id), { fallbackFilename: filename })
      const outcome = await shareDocument({
        title: t("share.titleQuotation", { number: quotation.number }),
        text: t("share.messageQuotation", {
          number: quotation.number,
          /*
           * THE QUOTED FIGURE, NOT THE GRAND TOTAL (§9.3). The WhatsApp message
           * and the attached sheet must say the same number — a message quoting
           * the tax-inclusive total against a TAX_EXTRA sheet that prints the
           * pre-tax one is a discrepancy the customer reads before opening the
           * attachment.
           */
          amount: formatRupees(quotation.display.printedGrandTotalPaise),
        }),
        file: { blob: fetched.blob, filename: fetched.filename ?? filename },
        phone: quotation.customer?.phone ?? null,
      })
      if (outcome.status === "fallback") toast.info(t("share.openedWhatsapp"))
      if (outcome.status === "failed") toast.error(t("share.noShareTarget"))

      /*
       * ═══════════════════════════════════════════════════════════════════════
       * IT ASKS. IT DOES NOT ASSUME.
       *
       * SENDING IT IS WHAT MAKES IT SENT (§7): the owner shares the sheet and
       * never returns to tick a status box, which is how a register fills with
       * offers permanently marked DRAFT — and DRAFT is the filter the dashboard's
       * "waiting for reply" count reads. So something has to stamp it.
       *
       * BUT A SHARE IS NOT EVIDENCE OF A SEND, and this briefly assumed it was.
       * `navigator.share` resolving means the OS accepted the hand-off, not that
       * the message left the phone; the `wa.me` fallback only OPENS WhatsApp and
       * never learns what happened next. The owner's own words: "if the sending
       * process fails we get a sent recorded for not being sent". A `sent_at` the
       * app invented is worse than none, because it is the date he will quote to
       * a customer who says the quotation never arrived.
       *
       * SO THE PROMPT IS THE RECORD. Yes stamps it, No leaves it alone.
       *
       * IT ASKS ONLY WHEN THE ANSWER IS UNKNOWN. A dismissed share sheet is him
       * changing his mind — that is a No he has already given, and asking again
       * teaches him to tap through. A failed share is an error, not a question.
       *
       * ONLY FROM DRAFT: re-marking an APPROVED quotation SENT would walk its
       * status backwards and lose the customer's answer because he forwarded the
       * sheet a second time.
       *
       * EMAIL IS DIFFERENT AND STAYS AUTOMATIC. The server does that send and
       * gets an SMTP accept-or-fail back, so "sent" is witnessed rather than
       * guessed. One is a fact; the other is a question.
       * ═══════════════════════════════════════════════════════════════════════
       */
      const worthAsking = outcome.status === "shared" || outcome.status === "fallback"
      if (quotation.status === "DRAFT" && worthAsking) {
        setAskSent(true)
      }
    } catch {
      toast.error(t("share.couldNotPrepare"))
    } finally {
      setSharing(false)
    }
  }

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * EMAIL — THE SAME ACT AS THE SHARE ABOVE, WITH THE SERVER DOING ALL OF IT.
   *
   * Note how much SHORTER this is than `onShare`, and that every difference is
   * the server holding a decision this client used to make:
   *
   *  • NO PDF IS FETCHED HERE. `onShare` downloads the sheet so the share sheet
   *    can carry a file; the API renders and attaches it itself, from the
   *    document's stored columns, so the customer receives the same bytes the
   *    View button would have shown.
   *  • NO RECIPIENT IS CHOSEN HERE. `onShare` passes the customer's phone for
   *    the `wa.me` fallback. The address is read off the customer row by the
   *    server, and the request has no body at all.
   *  • THE STATUS MOVE IS NOT THIS SCREEN'S EITHER. `onShare` fires a second
   *    request to mark a DRAFT as SENT, because the browser is the only thing
   *    that knows whether the share sheet was dismissed. The send either
   *    happened or it did not, so the server moves the status in the same
   *    transaction as the record and simply reports whether it did.
   *
   * THE ADDRESS IS SAID OUT LOUD IN THE TOAST. One button, and a message left
   * the building — "Sent" alone gives him no way to notice that it went to a
   * contact who left in March.
   *
   * THE SERVER'S REFUSALS ARE SHOWN VERBATIM, unlike the generic sentence
   * `onShare` falls back to. They are written to be read by the owner and to
   * name the fix ("Pashan Fabricators has no email address on file … Add one
   * under Customers → …"), and replacing that with "Could not send" would send
   * him hunting through settings for a blank field on one customer.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const onEmail = async () => {
    if (quotation === undefined || id === undefined) return
    try {
      const result = await email.mutateAsync()
      toast.success(t("share.emailSent", { address: result.to }))
      // Only when it actually moved — the server says so, and an APPROVED
      // quotation is never walked back to SENT by being forwarded again.
      if (result.statusChanged) toast.success(t("quotation.markedSentAfterShare"))
      void queryClient.invalidateQueries()
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("share.emailFailed"))
    }
  }

  if (query.isPending) return <DetailSkeleton />

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <BackLink />
        <Alert variant="destructive">
          <AlertTitle>{notFound ? t("err.notFound") : t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            {notFound ? null : (
              <Button
                variant="outline"
                onClick={() => {
                  void query.refetch()
                }}
              >
                {t("btn.retry")}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (quotation === undefined) return <DetailSkeleton />

  const voided = quotation.status === "VOID"
  const notice = LOCK_NOTICES[quotation.status]
  const canConvert = can("quotation.convert") && CONVERTIBLE.has(quotation.status)

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * ONCE THE CUSTOMER HAS SAID YES, MAKING THE BILL IS WHY YOU OPENED THIS PAGE.
   *
   * Conversion has always lived in a card at the FOOT of the screen, below the
   * lines and the totals — fine while the offer is still in play, and wrong the
   * moment it is approved, because then it is the only thing left to do and it
   * is below the fold.
   *
   * PROMOTED FOR APPROVED AND PARTIALLY_CONVERTED ONLY. Not DRAFT (the customer
   * has not seen it) and not SENT (they have not answered) — offering "Make a
   * bill" as the headline act on an unanswered offer invites billing work
   * nobody has agreed to pay for. PARTIALLY_CONVERTED is in, because there are
   * lines still to bill and "carry on billing" is exactly the next step.
   *
   * The card at the foot stays in every convertible state; this only adds a
   * second way in, and both open the SAME dialog.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const convertIsThePoint =
    canConvert && (quotation.status === "APPROVED" || quotation.status === "PARTIALLY_CONVERTED")

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight tabular-nums">
              {quotation.number}
            </h1>
            <DocumentStatusBadge status={quotation.status} kind="quotation" />
          </div>
          <p className="text-muted-foreground text-sm">
            {quotation.customer?.name ?? t("lbl.customerUnknown")} · {formatDay(quotation.date)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SyncStatus
            updatedAt={query.dataUpdatedAt}
            syncing={query.isFetching}
            onSync={() => {
              void query.refetch()
            }}
          />

          {/*
            * FIRST IN THE ROW, AND THE ONLY DARK BUTTON. An approved quotation
            * has one obvious next act; two competing primaries would make the
            * owner choose between them. "View the quotation" steps down to an
            * outline for exactly as long as that is true.
            */}
          {convertIsThePoint ? (
            <Button
              onClick={() => {
                setConverting(true)
              }}
            >
              <ArrowRightIcon />
              {t("quotation.convertAction")}
            </Button>
          ) : null}

          <Button
            variant={convertIsThePoint ? "outline" : undefined}
            onClick={() => {
              pdf.mutate()
            }}
            disabled={pdf.isPending}
          >
            <FileTextIcon />
            {pdf.isPending ? t("msg.loading") : t("btn.viewQuotation")}
          </Button>

          {/*
            * NO "COPIES" MENU, UNLIKE A BILL. §9.2's ORIGINAL / DUPLICATE /
            * TRIPLICATE captions are a tax-invoice requirement — the
            * transporter's copy travels with the goods. A quotation has no
            * statutory copies, and offering the choice would invent a rule.
            */}
          <NavigationMenu>
            <NavigationMenuList className="gap-2">
              <NavigationMenuItem>
                <NavigationMenuTrigger className={TRIGGER_AS_BUTTON} disabled={sharing}>
                  <Share2Icon className="size-4" />
                  {sharing ? t("share.preparing") : t("btn.share")}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="flex w-56 flex-col">
                    <li>
                      <NavigationMenuLink
                        render={
                          <button
                            type="button"
                            className="w-full text-left"
                            onClick={() => {
                              void onShare()
                            }}
                          />
                        }
                      >
                        <Share2Icon />
                        {t("btn.shareWhatsapp")}
                      </NavigationMenuLink>
                    </li>
                    {/*
                      * ═══════════════════════════════════════════════════════
                      * EMAIL — IN THIS MENU, BESIDE WHATSAPP, NOT ON A SCREEN
                      * OF ITS OWN (§17A.2 stage 1).
                      *
                      * It is the same act as the item above it: "send this
                      * quotation to the customer". What differs is the channel —
                      * and §17A.5 says why both stay: WhatsApp is the primary
                      * path for small local customers, email is for the
                      * corporate ones whose AP systems require it. A separate
                      * compose screen would make the choice feel like a
                      * different job and would need a recipient field, which is
                      * exactly the field this feature must not have.
                      *
                      * GATED ON `mail.send` THROUGH `can()` — the SAME string
                      * the route requires (§2.1), resolved through the same map
                      * in packages/types, so the button and the API cannot
                      * disagree. It HIDES rather than greys out: a disabled
                      * "Email" invites the question of how to enable it, and
                      * for someone without the capability the honest answer is
                      * that it is not theirs to do.
                      *
                      * NOT GATED ON THE `mail` FLAG HERE, and that is not an
                      * oversight: with the flag off the route answers 404 and
                      * this shows the API's own refusal. Hiding it on a flag
                      * would need a flag read on this screen for a control that
                      * costs one line — and §18.2's 404 is already the design
                      * for "this app does not have that".
                      * ═══════════════════════════════════════════════════════
                      */}
                    {can("mail.send") ? (
                      <li>
                        <NavigationMenuLink
                          render={
                            <button
                              type="button"
                              className="w-full text-left"
                              disabled={email.isPending}
                              onClick={() => {
                                void onEmail()
                              }}
                            />
                          }
                        >
                          <MailIcon />
                          {email.isPending ? t("share.emailSending") : t("btn.shareEmail")}
                        </NavigationMenuLink>
                      </li>
                    ) : null}
                    <li>
                      <NavigationMenuLink
                        render={
                          <button
                            type="button"
                            className="w-full text-left"
                            onClick={() => {
                              pdf.mutate()
                            }}
                          />
                        }
                      >
                        <DownloadIcon />
                        {t("btn.downloadPdf")}
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/*
            * EDIT — HIDDEN, NOT DISABLED, once the offer has been answered.
            * `isEditable` is the SERVER's answer (DRAFT or SENT only), and it
            * asserts the same rule again on the `PUT`, so this flag hides a
            * route and never authorises one.
            */}
          {can("quotation.create") && quotation.isEditable ? (
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to={routes.quotationEdit(quotation.id)} />}
            >
              <PencilIcon />
              {t("btn.edit")}
            </Button>
          ) : null}

          {can("quotation.create") ? (
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to={routes.quotationCopy(quotation.id)} />}
            >
              <CopyIcon />
              {t("btn.makeACopy")}
            </Button>
          ) : null}

          {/*
            * THE BILL IT BECAME — in the header with the other actions (owner's
            * ask, 2026-08-16), not buried in the status banner below where it
            * used to sit.
            *
            * "Converted" with no way to reach the invoice makes the owner search
            * the register for a number nobody has told him, so this link has to
            * exist. Where it sat was the problem: the banner is a STATEMENT
            * ("this became a bill"), and a control inside a sentence is a
            * different kind of thing from the row of controls six inches above
            * it. Every other way out of this document — share, edit, copy — is
            * in that row, and going somewhere is what this does too.
            *
            * NOT GATED ON A CAPABILITY, unlike its neighbours. Following a link
            * to a document is a READ, and §2.1 puts `invoice.view` in the shared
            * row every implemented role holds. The invoice screen re-checks it
            * on arrival regardless — this hides no route and authorises none.
            */}
          {quotation.convertedInvoiceId === null ? null : (
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to={routes.invoice(quotation.convertedInvoiceId)} />}
            >
              <FileTextIcon />
              {t("quotation.openInvoice")}
            </Button>
          )}
        </div>
      </header>

      {/*
        * THE STATE OF THE OFFER, SAID FIRST. A voided quotation keeps its number
        * and sits in the register beside offers that stand (§6), so on the
        * document itself there must be no chance of mistaking one for the other.
        */}
      {notice === undefined ? (
        quotation.status === "DRAFT" ? (
          <Alert>
            <InfoIcon />
            <AlertTitle>{t("quotation.draftTitle")}</AlertTitle>
            <AlertDescription>{t("quotation.draftHelp")}</AlertDescription>
          </Alert>
        ) : null
      ) : (
        <Alert variant={notice.danger ? "destructive" : "default"}>
          {notice.danger ? <TriangleAlertIcon /> : <InfoIcon />}
          <AlertTitle>{t(notice.titleKey)}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            <span>{t(notice.helpKey, { number: quotation.number })}</span>
            {voided && quotation.voidReason !== null ? (
              <span>{t("doc.cancelledReason", { reason: quotation.voidReason })}</span>
            ) : null}
            {/*
              * THE LINK TO THE BILL USED TO SIT HERE and moved up into the
              * header's action row on 2026-08-16. It is deliberately NOT in both
              * places: two buttons with one label on one screen make the owner
              * wonder what the difference is, and there is none.
              *
              * This banner keeps only the SENTENCE — what happened to the offer
              * — which is what a banner is for.
              */}
          </AlertDescription>
        </Alert>
      )}

      {pdf.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("doc.pdfFailedTitle")}</AlertTitle>
          <AlertDescription>
            {pdf.error instanceof ApiError ? pdf.error.message : t("doc.pdfFailedHelp")}
          </AlertDescription>
        </Alert>
      ) : null}

      {pdf.url === null ? null : <PdfPanel url={pdf.url} filename={pdf.filename} />}

      {/* The lifecycle row (§7) — only while the offer is still live. */}
      {can("quotation.create") && !voided && notice === undefined ? (
        <StatusCard quotation={quotation} />
      ) : null}

      {/*
        * §17.1 — THE CUSTOMER LINK AND "RECORD AS SENT". Hidden entirely without
        * `quotation.share` (the card decides that itself), and hidden on a
        * cancelled offer for the reason the share menu is: an offer that was
        * called off must not be put back in front of a customer.
        */}
      {voided ? null : (
        <QuotationApprovalCard
          quotationId={quotation.id}
          sentAt={quotation.sentAt}
          sentChannel={quotation.sentChannel}
          decidedAt={quotation.decidedAt}
          decidedByName={quotation.decidedByName}
          decisionNote={quotation.decisionNote}
        />
      )}

      {/*
        ═══════════════════════════════════════════════════════════════════════
        "WAS THIS SENT?" — the one thing the app cannot observe, so it asks.

        Opened only after a share that MIGHT have gone out. Yes stamps `sent_at`
        and the WhatsApp channel; No leaves the quotation exactly as it was, and
        he can share again or mark it sent by hand later.

        NOT `AlertDialogCancel` FOR "NO" AND NOTHING ELSE: both answers are real
        answers here, and the destructive-looking one is Yes — it writes a date a
        customer may be quoted. So neither is styled as the safe default.
        ═══════════════════════════════════════════════════════════════════════
      */}
      <AlertDialog open={askSent} onOpenChange={setAskSent}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("quotation.askSentTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("quotation.askSentBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("quotation.askSentNo")}</AlertDialogCancel>
            <Button
              onClick={() => {
                setAskSent(false)
                setStatus.mutate(
                  { status: "SENT", channel: "WHATSAPP" },
                  {
                    onSuccess: () => {
                      toast.success(t("quotation.markedSentAfterShare"))
                      void queryClient.invalidateQueries()
                    },
                  },
                )
              }}
            >
              {t("quotation.askSentYes")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DocumentFacts quotation={quotation} />

      <Card className="overflow-hidden py-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>{t("lbl.description")}</TableHead>
                <TableHead>HSN</TableHead>
                <TableHead className="text-right">{t("lbl.quantity")}</TableHead>
                <TableHead className="text-right">{t("lbl.rate")}</TableHead>
                <TableHead className="text-right">{t("lbl.taxRate")}</TableHead>
                <TableHead className="text-right">{t("lbl.amount")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotation.lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                    {t("doc.noLines")}
                  </TableCell>
                </TableRow>
              ) : (
                quotation.lines.map((line, index) => (
                  <LineRow key={line.id} line={line} index={index} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <QuotationTotalsCard quotation={quotation} />

      {quotation.notes === null && quotation.terms === null ? null : (
        <Card>
          <CardHeader>
            <CardTitle>{t("quotation.wordsTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            {quotation.notes === null ? null : (
              <div>
                <div className="text-muted-foreground text-xs">{t("lbl.notes")}</div>
                <p className="whitespace-pre-wrap">{quotation.notes}</p>
              </div>
            )}
            {quotation.terms === null ? null : (
              <div>
                <div className="text-muted-foreground text-xs">{t("lbl.terms")}</div>
                <p className="whitespace-pre-wrap">{quotation.terms}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {canConvert ? (
        <ConvertSection
          onOpen={() => {
            setConverting(true)
          }}
        />
      ) : null}

      {/* ONE DIALOG PER SCREEN, driven by whichever button was pressed. */}
      {canConvert ? (
        <ConvertDialog
          quotation={quotation}
          open={converting}
          onOpenChange={setConverting}
        />
      ) : null}

      {can("quotation.void") &&
      !CLOSED.has(quotation.status) &&
      quotation.convertedInvoiceId === null ? (
        <VoidSection
          quotation={quotation}
          onVoided={() => {
            // The register shows status and the dashboard counts open offers.
            void queryClient.invalidateQueries()
          }}
        />
      ) : null}
    </div>
  )
}

function BackLink(): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <Link
      to={routes.quotations}
      className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
    >
      <ArrowLeftIcon className="size-4" />
      {t("nav.quotations")}
    </Link>
  )
}

/** The PDF itself — inline on a big screen, a download on a phone. */
function PdfPanel({ url, filename }: { url: string; filename: string | null }): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Card className="overflow-hidden py-0">
      <CardHeader className="flex flex-row items-center justify-between gap-3 py-4">
        <CardTitle>{t("doc.pdfTitleQuotation")}</CardTitle>
        <Button
          variant="outline"
          nativeButton={false}
          render={<a href={url} download={filename ?? "quotation.pdf"} />}
        >
          <DownloadIcon />
          {t("btn.downloadPdf")}
        </Button>
      </CardHeader>
      <Separator />
      {/* `<object>` rather than `<iframe>`: it degrades to its children when the
        * platform has no inline PDF viewer, which is every mobile browser. */}
      <object data={url} type="application/pdf" className="hidden h-[70vh] w-full md:block">
        <p className="p-6 text-sm">{t("doc.pdfHelp")}</p>
      </object>
      <p className="text-muted-foreground p-4 text-sm md:hidden">{t("doc.pdfHelp")}</p>
    </Card>
  )
}

/**
 * Where the offer stands, and the one click that moves it (§7).
 *
 * THE CURRENT STATUS IS NOT OFFERED AS A BUTTON — pressing "Sent" on an offer
 * already marked sent would write an audit row recording a change that did not
 * happen. Neither is anything CONVERSION owns: CONVERTED and PARTIALLY_CONVERTED
 * are facts about an invoice existing, and the server refuses them here by name.
 */
function StatusCard({ quotation }: { quotation: QuotationDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const statusLabel = useStatusLabel()
  const queryClient = useQueryClient()
  const setStatus = useSetQuotationStatus(String(quotation.id))

  const options = NEXT_STATUSES.filter((option) => option.status !== quotation.status)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("quotation.statusTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {options.map((option) => (
            <Button
              key={option.status}
              variant="outline"
              title={t(option.helpKey)}
              disabled={setStatus.isPending}
              onClick={() => {
                setStatus.mutate(
                  { status: option.status },
                  {
                    onSuccess: () => {
                      /*
                       * THE NEW STATUS, IN WORDS. `quotation.statusSaved` is
                       * "Now shown as {{status}}." and this passed no value, so
                       * the toast printed the placeholder verbatim —
                       * "Now shown as {{status}}." — on screen, in front of the
                       * owner. i18next leaves an unfilled placeholder in place
                       * rather than erroring, so nothing failed and nothing said
                       * so.
                       *
                       * `statusLabel` is the same resolver the badge uses, kind
                       * and all, so the toast names the state using the words
                       * the row beside it is about to show.
                       */
                      toast.success(
                        t("quotation.statusSaved", {
                          status: statusLabel(option.status, "quotation"),
                        }),
                      )
                      void queryClient.invalidateQueries()
                    },
                  },
                )
              }}
            >
              {t(option.labelKey)}
            </Button>
          ))}
        </div>

        {setStatus.isError ? (
          <Alert variant="destructive">
            <AlertDescription>
              {setStatus.error instanceof ApiError
                ? setStatus.error.message
                : t("err.somethingWentWrong")}
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

function DocumentFacts({ quotation }: { quotation: QuotationDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const customer = quotation.customer

  const facts: { label: string; value: string | null }[] = [
    { label: t("lbl.subject"), value: quotation.subject },
    { label: t("lbl.gstin"), value: customer?.gstin ?? null },
    /*
     * THE BUYER'S PAN, exactly as the printed document derives it (§9.2).
     *
     * Registered → characters 3-12 of the GSTIN; unregistered → the Rule 114B
     * PAN typed on the customer. Never both: `customerSnapshot` writes
     * `pan: gstin === null ? customer.pan : null`, so the two are mutually
     * exclusive by construction and this `??` chain can only produce one.
     *
     * `panFromGstin` is the SHARED helper in `@steelbill/types`, the same one
     * the PDF uses — so the screen and the paper cannot disagree about a number
     * the buyer's accounts team will check.
     */
    { label: t("lbl.pan"), value: panFromGstin(customer?.gstin) ?? customer?.pan ?? null },
    {
      label: t("lbl.validUntil"),
      value: quotation.validUntil === null ? null : formatDay(quotation.validUntil),
    },
    { label: t("lbl.poRef"), value: quotation.poRefNo },
    /*
     * §9.3's `item no`, beside the P.O. reference it belongs to — the same
     * pairing the printed footer makes.
     *
     * ABSENT WHEN NULL, and that is right HERE even though the PDF keeps a blank
     * line for it. The sheet's blank exists to be written on with a pen; a screen
     * cannot be written on, so a labelled row with nothing after it would only
     * read as software that failed to print something (the `keyValueTable`
     * argument, from the other side). The `.filter` below drops it.
     */
    { label: t("lbl.itemNo"), value: quotation.itemNo },
    { label: t("lbl.contactName"), value: quotation.attnName },
    { label: t("lbl.vendorCode"), value: customer?.vendorCode ?? quotation.vendorCode },
    {
      label: t("quotation.taxModeLabel"),
      // §9.3, as the SERVER resolved it — never re-derived from the totals.
      value: quotation.display.taxIsExtra
        ? t("quotation.taxModeExtra")
        : t("quotation.taxModeComputed"),
    },
  ].filter((fact) => fact.value !== null && fact.value !== "")

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("doc.factsTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {customer === null ? null : (
          <address className="text-sm not-italic">
            <div className="font-medium">{customer.name}</div>
            {[customer.addressLine1, customer.addressLine2, customer.city, customer.pincode]
              .filter((part) => part !== null && part !== "")
              .map((part, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: address lines have no id.
                <div key={index} className="text-muted-foreground">
                  {part}
                </div>
              ))}
          </address>
        )}

        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {facts.map((fact) => (
            <div key={fact.label} className="flex justify-between gap-4 border-b pb-1">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="text-right font-medium">{fact.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}

function LineRow({ line, index }: { line: DocumentLine; index: number }): React.JSX.Element {
  const { t } = useTranslation()

  if (line.isGroupHeader) {
    return (
      <TableRow className="bg-muted/40">
        <TableCell className="tabular-nums">{line.lineCode ?? index + 1}</TableCell>
        <TableCell colSpan={5} className="font-medium">
          {line.description}
        </TableCell>
        <TableCell className="text-muted-foreground text-right text-xs">
          {t("a11y.headerAmount")}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <TableRow>
      <TableCell className="text-muted-foreground tabular-nums">
        {line.lineCode ?? index + 1}
      </TableCell>
      <TableCell className={line.parentLineId === null ? "" : "pl-8"}>
        {line.description}
        {/* §15(3)(a) — the line's own discount, under its description, exactly
          * where the printed sheet puts it (§9.2b). Never its apportioned share
          * of a whole-quotation discount: that is shown once, in the totals. */}
        {line.discountPaise > 0 ? (
          <span className="text-muted-foreground block text-xs tabular-nums">
            {t("line.discountApplied", { amount: formatRupees(line.discountPaise) })}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="tabular-nums">{line.hsn}</TableCell>
      <TableCell className="text-right tabular-nums whitespace-nowrap">
        {/* MILLI-UNITS → units, for DISPLAY only — see the bill screen's note. */}
        {(line.qtyMilli / 1000).toFixed(3).replace(/\.?0+$/, "")} {line.unitLabel}
      </TableCell>
      <TableCell className="text-right tabular-nums whitespace-nowrap">
        {formatRupees(line.ratePaise)}
      </TableCell>
      <TableCell className="text-right tabular-nums">{line.gstRateBp / 100}%</TableCell>
      <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
        {formatRupees(line.lineTotalPaise)}
      </TableCell>
    </TableRow>
  )
}

/**
 * The money, §9.3's way.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * UNDER TAX_EXTRA THE QUOTED FIGURE IS THE SUBTOTAL. The tax rows are still
 * shown — the customer is entitled to know what the GST will come to — but they
 * are labelled EXTRA, and the emphasised bottom line is
 * `display.printedGrandTotalPaise`, which is what the sheet prints and what the
 * customer was told.
 *
 * The tax-inclusive total is shown too, quietly, below the line: the owner needs
 * to know what the job will actually invoice at, and hiding it would send him to
 * a calculator on a figure the server has already computed correctly.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function QuotationTotalsCard({ quotation }: { quotation: QuotationDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const { totals, display } = quotation
  // The server decided the supply type (§5); this only renders its consequence.
  const interState = totals.igstPaise > 0

  const taxRows: { label: string; paise: number; show: boolean }[] = [
    { label: "CGST", paise: totals.cgstPaise, show: !interState },
    { label: "SGST", paise: totals.sgstPaise, show: !interState },
    { label: "IGST", paise: totals.igstPaise, show: interState },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("doc.totalsTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <dl className="ml-auto flex w-full max-w-sm flex-col gap-2 text-sm">
          {/*
            * §15(3)(a) in §9.2b's order, ABOVE the subtotal — `subtotalPaise`
            * is already NET and is what the tax was computed on. On a quotation
            * this is the figure that moved the customer's headline number: under
            * TAX_EXTRA the printed `G. Total` IS the pre-tax subtotal (§9.3).
            */}
          {totals.totalDiscountPaise > 0 ? (
            <>
              <div className="flex justify-between gap-6">
                <dt className="text-muted-foreground">{t("doc.beforeDiscount")}</dt>
                <dd className="tabular-nums">{formatRupees(totals.grossSubtotalPaise)}</dd>
              </div>
              <div className="flex justify-between gap-6">
                <dt className="text-muted-foreground">{t("doc.lessDiscount")}</dt>
                <dd className="tabular-nums">{formatRupees(totals.totalDiscountPaise)}</dd>
              </div>
            </>
          ) : null}

          <div className="flex justify-between gap-6">
            <dt className="text-muted-foreground">{t("lbl.subtotal")}</dt>
            <dd className="tabular-nums">{formatRupees(totals.subtotalPaise)}</dd>
          </div>

          {taxRows
            .filter((row) => row.show)
            .map((row) => (
              <div key={row.label} className="flex justify-between gap-6">
                <dt className="text-muted-foreground">
                  {row.label}
                  {display.taxIsExtra ? (
                    <span className="ml-1 text-xs uppercase">{t("quotation.taxExtra")}</span>
                  ) : null}
                </dt>
                <dd className="tabular-nums">{formatRupees(row.paise)}</dd>
              </div>
            ))}

          {totals.roundOffPaise === 0 || display.taxIsExtra ? null : (
            <div className="flex justify-between gap-6">
              <dt className="text-muted-foreground">{t("lbl.roundOff")}</dt>
              <dd className="tabular-nums">{formatRupees(totals.roundOffPaise)}</dd>
            </div>
          )}

          <Separator />

          <div className="flex justify-between gap-6 text-base font-semibold">
            <dt>{t("lbl.grandTotal")}</dt>
            {/* THE FIGURE THE CUSTOMER WAS QUOTED, chosen by the server. */}
            <dd className="tabular-nums">{formatRupees(display.printedGrandTotalPaise)}</dd>
          </div>

          {display.taxIsExtra ? (
            <div className="text-muted-foreground flex justify-between gap-6 text-xs">
              <dt>{t("quotation.withTaxLabel")}</dt>
              <dd className="tabular-nums">{formatRupees(totals.grandTotalPaise)}</dd>
            </div>
          ) : null}
        </dl>

        {totals.amountInWords === "" ? null : (
          <div className="mt-2 border-t pt-3 text-sm">
            <span className="text-muted-foreground">{t("doc.amountInWords")}: </span>
            {/* Worded by the SERVER, so this page and the printed sheet cannot
              * word the same number differently. */}
            <span className="font-medium">{totals.amountInWords}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Turning the offer into a bill (§7.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS ALLOCATES AN INVOICE NUMBER. It is the only button on this screen that
 * creates a tax document, and the number it consumes is spent whether or not the
 * owner meant it (§6) — so it sits behind a confirmation that says what will
 * happen, and the button is disabled for the whole flight of the request rather
 * than merely showing a spinner. A double-tap on a workshop connection would
 * otherwise raise two bills for one job.
 *
 * THE S.O. NUMBER IS ASKED FOR HERE, AND THAT IS THE POINT OF THE DIALOG.
 * §7.1: converting WITHOUT one is allowed but produces a DRAFT invoice, flagged
 * and waiting — never a blocked save, because the owner often knows the job is
 * won before the paperwork arrives. Such a draft is no longer stranded:
 * `PUT /invoices/:id` (`invoice.edit_draft`) completes the header afterwards,
 * and the bill screen offers it. Asking for the field at the one moment it is
 * free to supply is still the right thing — the draft's number is spent either
 * way — and the dialog says plainly what leaving it blank means.
 *
 * WHOLE-QUOTATION CONVERSION ONLY, FOR NOW. The API accepts `lineIds` for a
 * partial conversion (a staged job invoiced in instalments), and this screen
 * does not yet offer the line picker for it — so it sends no `lineIds` at all,
 * which the server reads as "all of them". Sending a partial set by accident
 * would leave lines silently unbilled.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * The card that explains conversion, at the foot of the screen.
 *
 * IT ONLY OPENS THE DIALOG — the dialog itself lives once, at screen level, so
 * the header's button and this one cannot drift into two conversion paths. One
 * of them would eventually forget the S.O. field, and a conversion without it
 * silently produces a DRAFT invoice that this app cannot yet issue (§7.1).
 */
function ConvertSection({
  onOpen,
}: {
  onOpen: () => void
}): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("quotation.convertTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-3">
        <p className="text-muted-foreground text-sm">{t("quotation.convertHelp")}</p>
        <Button onClick={onOpen}>
          <ArrowRightIcon />
          {t("quotation.convertAction")}
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * How long the dialog waits after a keystroke before re-running the dry run.
 *
 * The S.O. number is typed, and every character would otherwise be a POST on a
 * workshop connection. It is not a search box: one request per pause in typing
 * is the right rate for a confirmation the owner opens a few times a day.
 */
const PREVIEW_DEBOUNCE_MS = 400

/**
 * The dry run's inputs, settled — the pair rather than each field on its own.
 *
 * THEY MUST MOVE TOGETHER. `soDate` is only ever sent alongside its number
 * (§9.2 prints the pair or neither, and `QuotationConvertSchema` answers 400 for
 * a date alone). Debouncing the two separately would let a settled empty number
 * meet a live date for one render and post exactly that refused combination, so
 * this snapshots both at the same instant.
 */
function useSettledConvertInput(
  soNumber: string,
  soDate: string,
): { soNumber: string; soDate: string } {
  const [settled, setSettled] = useState({ soNumber, soDate })

  useEffect(() => {
    const timer = setTimeout(() => {
      setSettled({ soNumber, soDate })
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [soNumber, soDate])

  return settled
}

/**
 * Turning the offer into a bill — the dialog, rendered once per screen.
 *
 * CONTROLLED, because two buttons open it: this card at the foot of the page and
 * a promoted one in the header once the customer has approved the offer.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT SHOWS THE BILL BEFORE THE NUMBER IS SPENT. Conversion is a one-way door —
 * it allocates an invoice number and §6 never releases one, because a gap in the
 * series is a question from the department. This dialog used to ask the owner to
 * walk through that door having been shown no figure at all, under a standing
 * bullet that read "the bill is dated today, so it uses today's GST rates — not
 * the rates this quotation was priced at". True on every conversion, actionable
 * on none, and therefore the warning nobody reads: on the one conversion in
 * fifty where a rate HAD moved it said exactly the same thing.
 *
 * THAT BULLET IS GONE. `POST /:id/convert/preview` runs the same plan the
 * commit runs and writes nothing, so the dialog can show the real total, say
 * whether the bill would be issued or land as a draft, and — only when a rate
 * has actually moved — name the lines, both rates and what the move costs.
 * Silence about rates now MEANS something.
 *
 * THE CONFIRM BUTTON WAITS FOR IT. Confirming before the figures arrive is
 * precisely the blind commit this exists to remove. If the dry run FAILS the
 * button does not simply unlock: it changes into a differently-worded,
 * differently-styled one, so going ahead unchecked is a deliberate act rather
 * than the same tap he was about to make anyway.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function ConvertDialog({
  quotation,
  open,
  onOpenChange,
}: {
  quotation: QuotationDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [soNumber, setSoNumber] = useState("")
  const [soDate, setSoDate] = useState("")
  const convert = useConvertQuotation(String(quotation.id))

  /*
   * THE ONE PREDICATE IN THIS FILE, and it decides which FIELDS are available —
   * never what the bill's status would be. That question is the server's
   * (`conversionInvoiceStatus`, beside the commit that uses it) and comes back
   * as `wouldBeStatus`; re-deriving it here is how the dialog and the invoice
   * would eventually disagree about what counts as a blank S.O. number.
   */
  const hasSoNumber = soNumber.trim() !== ""
  const pairedSoDate = hasSoNumber && soDate !== "" ? soDate : ""

  const settled = useSettledConvertInput(soNumber, pairedSoDate)
  const preview = useConvertPreview(
    String(quotation.id),
    {
      soNumber: settled.soNumber,
      soDate: settled.soDate === "" ? undefined : settled.soDate,
      // No line picker on this screen yet, so no `lineIds`: the server reads
      // that as "every line not yet billed", which is the whole-quotation case.
    },
    open,
  )

  /*
   * HE HAS TYPED SINCE THE FIGURES ON SCREEN WERE ASKED FOR. Only
   * `wouldBeStatus` can differ — the money does not depend on the S.O. number —
   * but "would this be a draft?" is exactly what the field is for, so the
   * confirm button waits for the answer that matches what is in the box.
   */
  const previewIsStale = settled.soNumber !== soNumber || settled.soDate !== pairedSoDate
  const previewIsLoading = preview.isPending || preview.isFetching || previewIsStale
  const result = preview.data

  return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
          {/* WIDER AND SCROLLABLE. The stock popup is `max-w-xs` and unscrolled,
            * which was right when this held three bullets and two fields; it now
            * carries the figures the decision is made on, and a total clipped
            * off the bottom of a confirmation is worse than no total. */}
          <AlertDialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("quotation.convertConfirmTitle", { number: quotation.number })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("quotation.convertConfirmHelp")}
              </AlertDialogDescription>
            </AlertDialogHeader>

            {/*
              * `convertRatesToday` USED TO BE THE SECOND BULLET HERE AND IS NOT
              * COMING BACK. It printed on every conversion, which is what made
              * it invisible on the one that mattered. The rate question is now
              * answered with figures inside the panel below, and only when
              * there is something to answer.
              */}
            <ul className="text-muted-foreground flex list-disc flex-col gap-1.5 pl-5 text-sm">
              <li>{t("quotation.convertTakesNumber")}</li>
              <li>{t("quotation.convertKeepsQuotation", { number: quotation.number })}</li>
            </ul>

            {preview.isError ? (
              <Alert variant="destructive">
                <TriangleAlertIcon />
                <AlertTitle>{t("convertPreview.failedTitle")}</AlertTitle>
                <AlertDescription className="flex flex-col items-start gap-2">
                  <span>
                    {preview.error instanceof ApiError
                      ? preview.error.message
                      : t("convertPreview.failedHelp")}
                  </span>
                  {/* Nothing has been written, so nothing needs undoing — worth
                    * saying, because "could not check" reads as "something went
                    * wrong with my bill" otherwise. */}
                  <span>{t("convertPreview.failedNothingDone")}</span>
                  <Button
                    variant="outline"
                    onClick={() => {
                      void preview.refetch()
                    }}
                  >
                    {t("btn.retry")}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : result === undefined ? (
              <PreviewSkeleton />
            ) : (
              <ConvertPreviewPanel result={result} />
            )}

            {/*
              * §7.1 — the field that decides ISSUED versus DRAFT. Optional in
              * the schema and optional here; what is NOT optional is saying so,
              * which the note below the fields does.
              */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="convert-so-number">{t("lbl.soNumber")}</Label>
                <Input
                  id="convert-so-number"
                  value={soNumber}
                  onChange={(event) => {
                    setSoNumber(event.target.value)
                  }}
                  maxLength={64}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="convert-so-date">{t("lbl.soDate")}</Label>
                <DateField
                  id="convert-so-date"
                  value={soDate}
                  onChange={setSoDate}
                  clearable
                  /*
                   * A DATE WITH NO NUMBER IS REFUSED BY THE SCHEMA — it would
                   * print an "S.O. Date" row above an empty "S.O. No." row
                   * (§9.2). Disabling it until the number is typed turns that
                   * 400 into a field that simply is not available yet.
                   */
                  disabled={!hasSoNumber}
                />
              </div>
            </div>

            {convert.isError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  {convert.error instanceof ApiError
                    ? convert.error.message
                    : t("err.somethingWentWrong")}
                </AlertDescription>
              </Alert>
            ) : null}

            <AlertDialogFooter>
              <AlertDialogCancel render={<Button variant="outline" />}>
                {t("btn.cancel")}
              </AlertDialogCancel>
              <Button
                /*
                 * DISABLED UNTIL THE DRY RUN HAS ANSWERED for what is in the
                 * box. Not merely a spinner: the number is spent the instant
                 * this fires, and a double-tap on a workshop connection would
                 * raise two bills for one job.
                 *
                 * A FAILED dry run does NOT hold the button shut — he is
                 * entitled to convert without it — but it changes what the
                 * button says and how it looks, so going ahead unchecked is its
                 * own decision. See `convertPreview.goAhead`.
                 */
                variant={preview.isError ? "destructive" : undefined}
                disabled={convert.isPending || (previewIsLoading && !preview.isError)}
                onClick={() => {
                  /*
                   * NO `date` SENT. The server dates the invoice today in
                   * Asia/Kolkata, which decides its financial year and its GST
                   * rates (§8.7). Passing the QUOTATION's date — often weeks old
                   * — would resolve last quarter's rates onto a bill raised now,
                   * and in April it would file the bill in the wrong FY.
                   */
                  convert.mutate(
                    {
                      soNumber: hasSoNumber ? soNumber.trim() : undefined,
                      // Never a date without its number — the schema refuses it.
                      soDate: pairedSoDate === "" ? undefined : pairedSoDate,
                    },
                    {
                      onSuccess: (result) => {
                        onOpenChange(false)
                        toast.success(
                          t("quotation.convertedTo", { number: result.invoice.number }),
                        )
                        /*
                         * ═══════════════════════════════════════════════════
                         * THE COMMIT'S OWN WARNINGS, ABOUT THE BILL THAT NOW
                         * EXISTS. They were being thrown away — the hook
                         * returned `response.data` and the array went with the
                         * envelope — so §7.1's "Tax rate changed since this
                         * quotation. New total: ₹X (was ₹Y)." never reached a
                         * screen on the one path where nothing else says it.
                         *
                         * THAT PATH IS REACHABLE AND SUPPORTED. If the dry run
                         * fails (`retry: false`, so one network blip is final)
                         * the dialog offers "Make the bill without checking"
                         * and the owner is entitled to take it. The preview
                         * panel then never renders, the standing "today's GST
                         * rates" bullet is gone on purpose, and these
                         * sentences are the ONLY notice that a rate moved
                         * under a bill the customer has already approved.
                         *
                         * TOASTS, NOT PANEL TEXT, for the reason the invoice
                         * editor gives at its own `create.mutate`: this dialog
                         * closes and the screen navigates to the new bill a
                         * line below, and a toast survives that. `duration:
                         * Infinity` with a close button keeps a legal-validity
                         * notice on screen until he dismisses it — a
                         * four-second auto-hide is how a warning gets
                         * technically shown and never read.
                         *
                         * ENGLISH EVEN IN MARATHI, the same known gap the
                         * preview panel documents: they are composed
                         * server-side and §11.4's `t()` cannot reach them. The
                         * figures themselves are on the bill this navigates to.
                         * ═══════════════════════════════════════════════════
                         */
                        for (const warning of result.warnings) {
                          toast.warning(warning, { duration: Infinity, closeButton: true })
                        }
                        void queryClient.invalidateQueries()
                        void navigate(routes.invoice(result.invoice.id))
                      },
                    },
                  )
                }}
              >
                {convert.isPending
                  ? t("msg.saving")
                  : preview.isError
                    ? t("convertPreview.goAhead")
                    : previewIsLoading
                      ? t("convertPreview.checking")
                      : t("quotation.convertConfirmAction")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
  )
}

/** While the dry run is in flight. Sized to what replaces it, so nothing jumps. */
function PreviewSkeleton(): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3" data-testid="convert-preview-loading">
      <span className="text-muted-foreground text-sm">{t("convertPreview.checking")}</span>
      <Skeleton className="h-4 w-3/5" />
      <Skeleton className="h-4 w-2/5" />
      <Skeleton className="h-6 w-1/2" />
    </div>
  )
}

/**
 * The bill as it would be, before any of it exists.
 *
 * EVERY FIGURE HERE IS THE SERVER'S (§6) and every rupee amount goes through
 * `formatRupees`, so the dialog groups digits the Indian way exactly as the
 * printed invoice will.
 *
 * NO NUMBER IS SHOWN, and the payload carries none — not even a nullable one. A
 * caption mistakable for an allocated invoice number is the one promise a
 * preview must not make: this figure is advisory, the conversion re-runs the
 * same plan under a row lock, and that run is the authority.
 *
 * THE GRAND TOTAL, NOT §9.3's `printedGrandTotalPaise`. That distinction belongs
 * to a QUOTATION, which may quote a pre-tax figure with "GST EXTRA" against the
 * tax rows. This describes a tax INVOICE, where the tax is always computed and
 * the grand total is what the customer pays.
 */
function ConvertPreviewPanel({ result }: { result: ConvertPreviewResult }): React.JSX.Element {
  const { t } = useTranslation()
  const { preview } = result
  const { totals } = preview

  const rows: { label: string; paise: number; show: boolean }[] = [
    { label: t("lbl.subtotal"), paise: totals.subtotalPaise, show: true },
    // One line, not CGST/SGST/IGST separately: this is a decision aid, and the
    // split is on the document itself the moment it exists.
    { label: "GST", paise: totals.totalTaxPaise, show: true },
    { label: t("lbl.roundOff"), paise: totals.roundOffPaise, show: totals.roundOffPaise !== 0 },
  ]

  return (
    <div data-testid="convert-preview" className="flex flex-col gap-2 rounded-lg border p-3 text-sm">
      <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {t("convertPreview.title")}
      </div>

      <dl className="flex flex-col gap-1.5">
        <div className="flex justify-between gap-6">
          <dt className="text-muted-foreground">{t("convertPreview.linesLabel")}</dt>
          <dd className="tabular-nums">{preview.selectedLineCount}</dd>
        </div>
        {/*
          * §7.1's PARTIAL CONVERSION, and a row rather than a sentence so no
          * translation has to agree with English about plurals. Absent when
          * nothing is left over, which is the ordinary whole-quotation case.
          */}
        {preview.remainingLineCountAfter === 0 ? null : (
          <div className="flex justify-between gap-6">
            <dt className="text-muted-foreground">{t("convertPreview.remainingLabel")}</dt>
            <dd className="tabular-nums">{preview.remainingLineCountAfter}</dd>
          </div>
        )}
        {rows
          .filter((row) => row.show)
          .map((row) => (
            <div key={row.label} className="flex justify-between gap-6">
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className="tabular-nums">{formatRupees(row.paise)}</dd>
            </div>
          ))}
        <Separator />
        <div className="flex justify-between gap-6 text-base font-semibold">
          <dt>{t("lbl.grandTotal")}</dt>
          <dd className="tabular-nums">{formatRupees(totals.grandTotalPaise)}</dd>
        </div>
      </dl>

      {/*
        * ISSUED OR DRAFT, AS THE SERVER DECIDED IT. `wouldBeStatus` comes from
        * the same predicate the conversion applies to the same field, so this
        * sentence cannot promise something the commit then does differently.
        *
        * The server also sends `wouldBeDraftBecause`, a finished English
        * sentence. It is NOT printed: §11.4 puts every user-facing string
        * through `t()`, and the owner is the Marathi reader. The enum chooses
        * between two translated sentences instead, which is the same fact said
        * in his language.
        */}
      <p className="text-muted-foreground border-t pt-2">
        {preview.wouldBeStatus === "ISSUED"
          ? t("quotation.convertWillIssue")
          : t("quotation.convertWillDraft")}
      </p>

      {preview.rateChanges.length === 0 ? null : (
        <RateChangeNotice
          changes={preview.rateChanges}
          quotedGrandTotalPaise={preview.quotedTotals?.grandTotalPaise ?? null}
          invoicedGrandTotalPaise={totals.grandTotalPaise}
        />
      )}

      {/*
        * THE SERVER'S OWN LIST, VERBATIM AND UNFILTERED — the same treatment the
        * invoice editor's totals bar gives its preview warnings, and for the
        * same reason: these are the server saying the document has a real
        * problem (the buyer's details have moved since the quotation was
        * frozen; the offer was rejected), and the client is in no position to
        * decide which of them the owner may skip.
        *
        * Amber rather than muted grey. Attention, not error — nothing has gone
        * wrong yet, and nothing will until he presses the button.
        *
        * ENGLISH, EVEN IN MARATHI, and that is a known gap rather than an
        * oversight: they are generated server-side and §11.4's `t()` cannot
        * reach them. The facts that MUST cross the language barrier — the
        * total, the rates, the draft-or-issued verdict — are all rendered above
        * through `t()` and `formatRupees`, so nothing load-bearing depends on
        * reading these.
        */}
      {result.warnings.length === 0 ? null : (
        <ul data-testid="convert-preview-warnings" className="flex flex-col gap-1 border-t pt-2">
          {result.warnings.map((warning) => (
            <li key={warning} className="text-xs font-medium text-amber-600 dark:text-amber-500">
              {warning}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * §8.7 — a rate moved between the quotation and today, said ONLY when it did.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS THE WHOLE POINT OF THE DRY RUN. The gap between quoting and billing is
 * about three weeks, and the rates on the bill are resolved as of the bill's
 * date, never the quotation's. Before this, the owner learned that a rate had
 * moved from a warning attached to an invoice that already existed and already
 * held a number.
 *
 * BOTH RATES AND THE MONEY, per line. "The rate changed" alone leaves him to
 * work out whether it matters; `differencePaise` is the answer, computed by the
 * server as one integer subtraction between two figures the tax engine produced.
 * NEGATIVE when the rate fell — `formatRupees` prints the sign.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function RateChangeNotice({
  changes,
  quotedGrandTotalPaise,
  invoicedGrandTotalPaise,
}: {
  changes: ConversionPreviewRateChange[]
  quotedGrandTotalPaise: number | null
  invoicedGrandTotalPaise: number
}): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Alert variant="destructive" data-testid="convert-rate-changes">
      <TriangleAlertIcon />
      <AlertTitle>{t("convertPreview.rateMovedTitle")}</AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-2">
        <span>{t("convertPreview.rateMovedHelp")}</span>
        <ul className="flex w-full flex-col gap-1.5">
          {changes.map((change) => (
            <li key={change.lineId} className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="min-w-0 flex-1 truncate">{change.description}</span>
              <span className="tabular-nums whitespace-nowrap">
                {/*
                  * BASIS POINTS → percent, for DISPLAY only — `1800` is 18%,
                  * the same division the line table does. Nothing reads it back
                  * and no money is computed from it here or anywhere in this
                  * app: the difference beside it was worked out server-side.
                  */}
                {t("convertPreview.rateWas", {
                  was: change.quotedRateBp / 100,
                  now: change.invoicedRateBp / 100,
                })}
              </span>
              <span className="tabular-nums whitespace-nowrap">
                {formatRupees(change.differencePaise)}
              </span>
            </li>
          ))}
        </ul>
        {quotedGrandTotalPaise === null ? null : (
          <span>
            {t("convertPreview.totalWas", {
              now: formatRupees(invoicedGrandTotalPaise),
              was: formatRupees(quotedGrandTotalPaise),
            })}
          </span>
        )}
      </AlertDescription>
    </Alert>
  )
}

/**
 * Withdrawing the offer. Last, alone, and terminal.
 *
 * THE REASON IS MANDATORY (3–500 characters, enforced by the API too). The
 * number stays in a gap-free series, and "why is 542/26 void?" is a question
 * somebody asks a year later with nobody able to answer it otherwise.
 *
 * NOT OFFERED ONCE A BILL HAS BEEN RAISED. Voiding the offer behind an issued
 * invoice would leave the bill pointing at a withdrawn document; the correction
 * path for a bill is a credit note (§17.3), never a change to its quotation.
 */
function VoidSection({
  quotation,
  onVoided,
}: {
  quotation: QuotationDetail
  onVoided: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState("")
  const voidIt = useVoidQuotation(String(quotation.id))

  const tooShort = reason.trim().length < 3

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive flex items-center gap-2 text-base">
          <OctagonAlertIcon aria-hidden="true" className="size-4 shrink-0" />
          {t("voidQuotation.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start gap-3">
        {/*
          * `{{number}}` IS SUPPLIED. The string reads "…keeps number {{number}},
          * so nothing is missing from your quotation numbers" — the sentence
          * exists to promise §5's series stays gap-free, and it was called bare,
          * so i18next (`skipOnVariables` defaults to TRUE) left the braces in
          * place and the owner read `keeps number {{number}}` at the moment he
          * was being asked to confirm an irreversible cancel. Same class as the
          * 2026-08-17 `counters.nowInvoice` bug, opposite direction; the i18n
          * guard now fails on a bare call whose string carries a placeholder.
          */}
        <p className="text-muted-foreground text-sm">
          {t("voidQuotation.help", { number: quotation.number })}
        </p>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger
            render={
              <Button
                variant="outline"
                className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
              />
            }
          >
            <OctagonAlertIcon />
            {t("voidQuotation.action")}
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("voidQuotation.confirmTitle", { number: quotation.number })}
              </AlertDialogTitle>
              <AlertDialogDescription>{t("voidQuotation.confirmHelp")}</AlertDialogDescription>
            </AlertDialogHeader>

            <ul className="text-muted-foreground flex list-disc flex-col gap-1.5 pl-5 text-sm">
              <li>{t("voidQuotation.keepsNumber", { number: quotation.number })}</li>
              <li>{t("voidQuotation.cannotUndo")}</li>
            </ul>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="void-reason">{t("voidQuotation.reasonLabel")}</Label>
              <Textarea
                id="void-reason"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
                maxLength={500}
                rows={3}
              />
              <p className="text-muted-foreground text-xs">{t("voidQuotation.reasonHelp")}</p>
            </div>

            {voidIt.isError ? (
              <Alert variant="destructive">
                <AlertDescription>
                  {voidIt.error instanceof ApiError
                    ? voidIt.error.message
                    : t("err.somethingWentWrong")}
                </AlertDescription>
              </Alert>
            ) : null}

            <AlertDialogFooter>
              <AlertDialogCancel render={<Button variant="outline" />}>
                {t("voidQuotation.keepIt")}
              </AlertDialogCancel>
              <Button
                variant="destructive"
                disabled={tooShort || voidIt.isPending}
                onClick={() => {
                  voidIt.mutate(reason.trim(), {
                    onSuccess: () => {
                      setOpen(false)
                      toast.success(t("voidQuotation.done", { number: quotation.number }))
                      onVoided()
                    },
                  })
                }}
              >
                {t("voidQuotation.confirmAction")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

function DetailSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  )
}
